import React, {FC} from 'react';

import {ROUTE} from 'containers/App/constants';
import {
  Label,
  Title,
  ErrorMessage,
  PrimaryButton,
  LinkButton,
} from 'components';
import {validate as isEmail} from 'isemail';
import i18n from 'i18next';
import {RegisterOptions, useForm} from 'react-hook-form';
import {makeBorderClass} from 'components/Form/Input';

interface FormProps {
  onSignIn: (email: string, password: string) => void;
  serverError?: string;
}

interface Inputs {
  email: string;
  password: string;
}

const validationRules: Record<keyof Inputs, RegisterOptions> = {
  email: {
    required: true,
    validate: value => isEmail(value),
  },
  password: {
    required: true,
  },
};

const Form: FC<FormProps> = ({onSignIn, serverError}) => {
  const {register, handleSubmit, errors} = useForm<Inputs>();

  const onSubmit = handleSubmit(({email, password}) => {
    onSignIn(email, password);
  });

  return (
    <form
      className="bg-white rounded-gigantic py-6 px-10 flex flex-col z-index-50 relative"
      onSubmit={onSubmit}
    >
      <Title className="text-3xl">{i18n.t('loginPage.loginTitle')}</Title>

      {/* Email field and errors */}

      <Label
        htmlFor="email"
        label={i18n.t('common.emailAddressLabel')}
        className="mt-8"
      >
        <input
          id="email"
          type="text"
          name="email"
          className={`w-full h-12 px-4 py-2 ${makeBorderClass()}`}
          ref={register(validationRules.email)}
        />
      </Label>
      {errors.email?.type === 'required' && (
        <ErrorMessage className="mt-3">
          {i18n.t('loginPage.validationErrors.noEmail')}
        </ErrorMessage>
      )}
      {errors.email?.type === 'validate' && (
        <ErrorMessage className="mt-3">
          {i18n.t('loginPage.validationErrors.invalidEmail')}
        </ErrorMessage>
      )}

      {/* Password field and errors */}

      <Label
        htmlFor="password"
        label={i18n.t('common.passwordLabel')}
        className="mt-3"
      >
        <input
          id="password"
          type="password"
          name="password"
          className={`w-full h-12 px-4 py-2 ${makeBorderClass()}`}
          ref={register(validationRules.password)}
        />
      </Label>
      {errors.password?.type === 'required' && (
        <ErrorMessage className="mt-3">
          {i18n.t('loginPage.validationErrors.noPassword')}
        </ErrorMessage>
      )}

      {/* Submit button */}

      <PrimaryButton type="submit" className="w-full h-12 mt-4">
        {i18n.t('loginPage.login')}
      </PrimaryButton>

      {/* Potential server error */}

      {serverError && (
        <ErrorMessage className="mt-4">
          {i18n.t('loginPage.validationErrors.incorrectEmailOrPassword')}
        </ErrorMessage>
      )}

      {/* Forgot password button */}

      <div className="text-right mt-4">
        <LinkButton to={ROUTE.ResetPassword}>
          {i18n.t('loginPage.forgotPassword')}
        </LinkButton>
      </div>
    </form>
  );
};

export default Form;
